// WebAuthn helper utilities for ArrayBuffer <-> base64url conversion

export const bufferToBase64url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const base64urlToBuffer = (base64url: string): ArrayBuffer => {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Decode server response into PublicKeyCredentialRequestOptions for navigator.credentials.get()
interface CredentialRequestOptionsInput {
  challenge: string;
  allowCredentials?: Array<{ id: string; type: string; transports?: AuthenticatorTransport[] }>;
  timeout?: number;
  rpId?: string;
  userVerification?: UserVerificationRequirement;
}

export const prepareCredentialRequestOptions = (
  options: CredentialRequestOptionsInput
): PublicKeyCredentialRequestOptions => {
  const prepared: PublicKeyCredentialRequestOptions = {
    challenge: base64urlToBuffer(options.challenge),
    timeout: options.timeout,
    rpId: options.rpId,
    userVerification: options.userVerification,
  };

  if (options.allowCredentials) {
    prepared.allowCredentials = options.allowCredentials.map((cred) => ({
      id: base64urlToBuffer(cred.id),
      type: cred.type as PublicKeyCredentialType,
      transports: cred.transports,
    }));
  }

  return prepared;
};

// Decode server response into PublicKeyCredentialCreationOptions for navigator.credentials.create()
interface CredentialCreationOptionsInput {
  challenge: string;
  rp: PublicKeyCredentialRpEntity;
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  excludeCredentials?: Array<{ id: string; type: string; transports?: AuthenticatorTransport[] }>;
  timeout?: number;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
}

export const prepareCredentialCreationOptions = (
  options: CredentialCreationOptionsInput
): PublicKeyCredentialCreationOptions => {
  const prepared: PublicKeyCredentialCreationOptions = {
    challenge: base64urlToBuffer(options.challenge),
    rp: options.rp,
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id),
    },
    pubKeyCredParams: options.pubKeyCredParams,
    timeout: options.timeout,
    authenticatorSelection: options.authenticatorSelection,
    attestation: options.attestation,
  };

  if (options.excludeCredentials) {
    prepared.excludeCredentials = options.excludeCredentials.map(
      (cred) => ({
        id: base64urlToBuffer(cred.id),
        type: cred.type as PublicKeyCredentialType,
        transports: cred.transports,
      })
    );
  }

  return prepared;
};

// Serialize a PublicKeyCredential into a plain object with base64url-encoded fields for sending to server
export const serializeCredential = (
  credential: PublicKeyCredential
): object => {
  const response = credential.response;

  const serialized: {
    id: string;
    rawId: string;
    type: string;
    response: Record<string, string>;
  } = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {},
  };

  if ("attestationObject" in response) {
    // Registration response (AuthenticatorAttestationResponse)
    const attestation = response as AuthenticatorAttestationResponse;
    serialized.response = {
      clientDataJSON: bufferToBase64url(attestation.clientDataJSON),
      attestationObject: bufferToBase64url(attestation.attestationObject),
    };
  } else {
    // Authentication response (AuthenticatorAssertionResponse)
    const assertion = response as AuthenticatorAssertionResponse;
    serialized.response = {
      clientDataJSON: bufferToBase64url(assertion.clientDataJSON),
      authenticatorData: bufferToBase64url(assertion.authenticatorData),
      signature: bufferToBase64url(assertion.signature),
      ...(assertion.userHandle && {
        userHandle: bufferToBase64url(assertion.userHandle),
      }),
    };
  }

  return serialized;
};
